import { createFileRoute } from '@tanstack/react-router';
import {
  Alert,
  Anchor,
  Button,
  Card,
  Group,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

import en from '../../i18n/en.json';
import hi from '../../i18n/hi.json';

const BUNDLED: Record<string, Record<string, string>> = { en, hi };

function TranslationsPage() {
  const languages = trpc.admin.languages.list.useQuery();
  const [locale, setLocale] = useState<string>('en');

  const overrides = trpc.admin.translations.get.useQuery({ locale }, { enabled: !!locale });
  const utils = trpc.useUtils();
  const save = trpc.admin.translations.set.useMutation({
    onSuccess: () => {
      void utils.admin.translations.get.invalidate({ locale });
      notifications.show({ message: 'Saved overrides', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');

  const bundled = BUNDLED[locale] ?? BUNDLED['en']!;

  const merged = useMemo(() => {
    // Always show bundled English keys as the row scaffold so the SuperAdmin
    // can write overrides for any locale, even if no bundled file exists yet.
    const out: Record<string, string> = { ...bundled };
    for (const [k, v] of Object.entries(overrides.data ?? {})) out[k] = v;
    return out;
  }, [bundled, overrides.data]);

  useEffect(() => {
    setDraft({});
  }, [locale]);

  const rows = useMemo(() => {
    const all = Object.entries(merged);
    return filter
      ? all.filter(
          ([k, v]) =>
            k.toLowerCase().includes(filter.toLowerCase()) ||
            v.toLowerCase().includes(filter.toLowerCase()),
        )
      : all;
  }, [merged, filter]);

  const localeOptions = useMemo(
    () =>
      (languages.data ?? []).map((l) => ({
        value: l.code,
        label: l.nativeName ? `${l.name} (${l.nativeName})` : l.name,
      })),
    [languages.data],
  );

  const hasBundle = !!BUNDLED[locale];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Translations</Title>
          <Select
            data={localeOptions}
            value={locale}
            onChange={(v) => v && setLocale(v)}
            placeholder="Select locale"
            searchable
            w={260}
            disabled={languages.isPending}
          />
        </Group>

        {!hasBundle && (
          <Alert color="orange" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">
              No bundled <code>{locale}.json</code> exists in the web app — the row scaffold below
              uses English keys. Anything you save here is stored as an override under
              <code> i18n:{locale}</code> in the Configuration table; the web app will merge it over
              English at runtime. <Anchor href="/central/languages">Manage languages</Anchor>
            </Text>
          </Alert>
        )}

        <TextInput
          placeholder="Filter by key or value…"
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
        />
        <Card p={0}>
          {overrides.isPending ? (
            <Skeleton height={300} m="md" />
          ) : (
            <Table withRowBorders={false} striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '30%' }}>Key</Table.Th>
                  <Table.Th>Bundled value</Table.Th>
                  <Table.Th>Override</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map(([k, v]) => {
                  const overrideVal = draft[k] ?? overrides.data?.[k] ?? '';
                  return (
                    <Table.Tr key={k}>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {k}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {bundled[k] ?? v}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          value={overrideVal}
                          onChange={(e) => setDraft({ ...draft, [k]: e.currentTarget.value })}
                          placeholder="(no override)"
                        />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Card>
        <Group>
          <Button
            loading={save.isPending}
            onClick={() => {
              const toWrite: Record<string, string | null> = {};
              for (const [k, v] of Object.entries(draft)) {
                toWrite[k] = v.trim() === '' ? null : v;
              }
              save.mutate({ locale, entries: toWrite });
            }}
          >
            Save overrides
          </Button>
        </Group>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/central/translations')({ component: TranslationsPage });
